import WheelPainter from './WheelPainter.js'
import DisplayEntryPicker from './DisplayEntryPicker.js'
import * as Util from './Util.js'

export default class Wheel {
  constructor() {
    this.angle = 0
    this.speed = 0
    this.state = new InitialDemoSpinState(this)
    this.wheelPainter = new WheelPainter()
    this.entryPicker = new DisplayEntryPicker()
    this.doneSpinningCallback = () => {}
    this.nameChangedCallback = () => {}
    this.riggingEnabled = false
    this.rigTargetEntryId = null
    this.visualRigMode = false
    this.originalEntries = null
    this.visualRigActive = false
  }

  setEntries(entries, maxSlices, allowDuplicates) {
    if (!this.state.isSpinning()) {
      const enabledEntries = entries.filter(
        (entry) => entry.enabled || !entry.hasOwnProperty('enabled'),
      )
      this.entryPicker.setEntries(enabledEntries, maxSlices, allowDuplicates)
      this.wheelPainter.refresh()
    }
  }

  refresh() {
    this.wheelPainter.refresh()
  }

  configure(
    wheelConfig,
    darkMode,
    riggingEnabled = false,
    rigTargetEntryId = null,
    visualRigMode = false,
  ) {
    if (!this.state.isSpinning()) {
      this.wheelConfig = wheelConfig
      this.darkMode = darkMode
      this.riggingEnabled = riggingEnabled
      this.rigTargetEntryId = rigTargetEntryId
      this.visualRigMode = visualRigMode
      this.wheelPainter.refresh()
    }
  }

  tick() {
    this.state.tick(this)
    this.advance()
    const updated = this.entryPicker.tick(this.getIndexAtPointer())
    if (updated) this.wheelPainter.refresh()
  }

  click(nameChangedCallback, doneSpinningCallback) {
    this.nameChangedCallback = nameChangedCallback
    this.doneSpinningCallback = doneSpinningCallback
    // Restore original entries if visual rig was active - use current wheelConfig entries
    if (this.visualRigActive) {
      const entriesToRestore = this.wheelConfig
        ? this.wheelConfig.entries
        : this.originalEntries || []
      this.entryPicker.setEntries(
        entriesToRestore,
        this.wheelConfig.maxNames,
        this.wheelConfig.allowDuplicates,
      )
      this.visualRigActive = false
      this.originalEntries = null
    }
    this.state.click(this)
  }

  spinIsDone() {
    this.doneSpinningCallback(this.getEntryAtPointer())
  }

  isSpinning() {
    return this.state.isSpinning()
  }

  setRandomPosition() {
    this.angle = Math.random() * 2 * Math.PI
    this.entryPicker.setRandomPosition()
  }

  // VISUAL RIG: Swap all entries to copies of target entry
  setVisualRigEntries(targetEntryId, currentEntries) {
    const entries =
      currentEntries ||
      (this.entryPicker.getAllEntries ? this.entryPicker.getAllEntries() : [])
    const targetEntry = entries.find((e) => e.id === targetEntryId)

    if (!targetEntry) return

    this.originalEntries = currentEntries ? [...currentEntries] : [...entries]

    const riggedEntries = []
    const numEntries = entries.length
    console.log(`Visual rig: Creating ${numEntries} copies of target entry`)
    for (let i = 0; i < numEntries; i++) {
      riggedEntries.push({
        ...targetEntry,
        id: `${targetEntry.id}_rig_${i}`,
        text: targetEntry.text,
      })
    }

    const canvas = document.getElementById('wheelCanvas')
    if (canvas) {
      canvas.style.transition = 'opacity 0.15s ease'
      canvas.style.opacity = '0.5'

      setTimeout(() => {
        this.entryPicker.setEntries(
          riggedEntries,
          this.wheelConfig.maxNames,
          this.wheelConfig.allowDuplicates,
        )
        this.visualRigActive = true
        this.wheelPainter.refresh()

        setTimeout(() => {
          canvas.style.opacity = '1'
        }, 50)
      }, 150)
    } else {
      this.entryPicker.setEntries(
        riggedEntries,
        this.wheelConfig.maxNames,
        this.wheelConfig.allowDuplicates,
      )
      this.visualRigActive = true
      this.wheelPainter.refresh()
    }
  }

  // RIGGING: Calculate starting angle so deceleration lands on target
  setRiggedPosition(targetEntryId) {
    const entries = this.entryPicker.getDisplayEntries()
    const targetIndex = entries.findIndex((e) => e.id === targetEntryId)

    if (targetIndex === -1) {
      this.setRandomPosition()
      return
    }

    const radiansPerSegment = (2 * Math.PI) / entries.length

    // Calculate total rotation during deceleration phase
    const timeLengths = this.getStateTimeLengths()
    const decelTicks = timeLengths.decelerating

    // Current speed is the peak speed after acceleration
    const startSpeed = this.speed
    const stopSpeed = 0.00015
    const deceleration = Math.exp(Math.log(stopSpeed / startSpeed) / decelTicks)

    // Calculate total rotation during deceleration (sum of geometric series)
    const totalDecelRotation = (startSpeed - stopSpeed) / (1 - deceleration)

    // Where the wheel currently is: this.angle
    // After rotating totalDecelRotation more, we want to land on target
    // Target position: targetIndex * radiansPerSegment
    // So: (this.angle + totalDecelRotation) mod 2π should equal targetIndex * radiansPerSegment
    // Therefore, we need to adjust this.angle so:
    // this.angle = (targetIndex * radiansPerSegment - totalDecelRotation) mod 2π

    const targetAngle = targetIndex * radiansPerSegment
    let newAngle = targetAngle - totalDecelRotation

    // Normalize to 0-2π
    while (newAngle < 0) newAngle += 2 * Math.PI
    while (newAngle >= 2 * Math.PI) newAngle -= 2 * Math.PI

    // Add small random variation for natural feel (±5% of a segment)
    const variation = (Math.random() - 0.5) * 0.1 * radiansPerSegment
    newAngle += variation

    // Normalize again
    while (newAngle < 0) newAngle += 2 * Math.PI
    while (newAngle >= 2 * Math.PI) newAngle -= 2 * Math.PI

    this.angle = newAngle
    this.entryPicker.setRandomPosition()
  }

  advance() {
    this.indexFromLastTick = this.indexFromThisTick
    this.indexFromThisTick = this.getIndexAtPointer()
    if (this.indexFromThisTick != this.indexFromLastTick) {
      this.nameChangedCallback()
    }
    this.angle += this.speed
    if (this.angle > Math.PI * 2) {
      this.angle -= Math.PI * 2
    }
  }

  getIndexAtPointer() {
    return Util.getIndexAtPointer(
      this.entryPicker.getDisplayEntries(),
      this.angle,
    )
  }

  getEntryAtPointer() {
    return this.entryPicker.getDisplayEntries()[this.getIndexAtPointer()]
  }

  resetRotation() {
    this.angle = 0
  }

  getStateTimeLengths() {
    const retVal = { accelerating: 0, decelerating: 0 }
    const spinTicks = this.wheelConfig.spinTime * 60
    retVal.accelerating = Math.min(60, spinTicks / 3)
    retVal.decelerating = spinTicks - retVal.accelerating
    return retVal
  }

  draw(context) {
    if (this.wheelConfig && this.state.drawThisFrame()) {
      this.wheelPainter.draw(
        context,
        this.angle,
        this.entryPicker.getDisplayEntries(),
        this.entryPicker.getAllEntries(),
        this.wheelConfig,
        this.darkMode,
      )
    }
  }
}

class InitialDemoSpinState {
  constructor(wheel) {
    wheel.speed = 0.005
  }

  tick(wheel) {}

  click(wheel) {
    wheel.state = new AcceleratingState(wheel)
  }

  isSpinning() {
    return false
  }

  drawThisFrame() {
    return true
  }
}

class AcceleratingState {
  constructor(wheel) {
    this.age = 0
    this.MAX_AGE = wheel.getStateTimeLengths().accelerating
  }

  tick(wheel) {
    const acceleration = wheel.wheelConfig.slowSpin ? 0.001 : 0.01
    wheel.speed += acceleration
    this.age += 1
    if (this.age > this.MAX_AGE) {
      // VISUAL RIG: Swap entries at max velocity
      if (wheel.visualRigMode && wheel.rigTargetEntryId) {
        // Pass current entries from wheelConfig to ensure we capture all names
        const currentEntries = wheel.wheelConfig
          ? wheel.wheelConfig.entries
          : []
        wheel.setVisualRigEntries(wheel.rigTargetEntryId, currentEntries)
        wheel.setRandomPosition()
      }
      // ANGLE RIG: Use rigged position if enabled
      else if (wheel.riggingEnabled && wheel.rigTargetEntryId) {
        wheel.setRiggedPosition(wheel.rigTargetEntryId)
      } else {
        wheel.setRandomPosition()
      }
      wheel.state = new DeceleratingState(wheel)
    }
  }

  click(wheel) {}

  isSpinning() {
    return true
  }

  drawThisFrame() {
    return true
  }
}

class DeceleratingState {
  constructor(wheel) {
    this.age = 0
    this.MAX_AGE = wheel.getStateTimeLengths().decelerating
    const decelTicks = this.MAX_AGE
    const startSpeed = wheel.speed
    const stopSpeed = 0.00015
    this.deceleration = Math.exp(Math.log(stopSpeed / startSpeed) / decelTicks)
  }

  tick(wheel) {
    wheel.speed = wheel.speed * this.deceleration
    this.age += 1
    if (this.age > this.MAX_AGE) {
      wheel.state = new PostSpinState(wheel)
    }
  }

  click(wheel) {}

  isSpinning() {
    return true
  }

  drawThisFrame() {
    return true
  }
}

class PostSpinState {
  constructor(wheel) {
    wheel.speed = 0
    wheel.spinIsDone()
  }

  tick(wheel) {}

  click(wheel) {
    wheel.state = new AcceleratingState(wheel)
  }

  isSpinning() {
    return false
  }

  drawThisFrame() {
    return true
  }
}
